import { RecipeSearchBar } from "@/components/RecipeSearchBar/RecipeSearchBar";
import { useGetRecipeTypes } from "@/hooks/useGetRecipeTypes";
import { useGetAppliedFiltersFromLocation, type FilterParams } from "@/hooks/useGetAppliedFiltersFromLocation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Filters } from "./filters-renamed/Filters";
import { useForm } from "react-hook-form";
import { Button } from "@heroui/react";
import { useScrollToResults } from "./useScrollToResults";
import { RecipeType } from "@/lib/types";
import { useGetRecipeCategories } from "@/hooks/useGetRecipeCategories";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/react/24/solid";
import { ChevronDownIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { ActiveFilterChips } from "./filters-renamed/ActiveFilterChips";

const defaultEmptyFilters: RecipesFilterFormValues = {
    recipeTypeNames: [],
    recipeCategoryNames: [],
    isFavorite: false,
    hasMadeIt: ["any"]
};

function parseDefaultRecipeFilterValues(appliedFilters: FilterParams, fixedType?: string): RecipesFilterFormValues {
    let hasMadeIt = ["any"];
    if (appliedFilters.hasMadeIt === "true" || appliedFilters.hasMadeIt === "false") {
        hasMadeIt = [appliedFilters.hasMadeIt];
    }

    return {
        recipeTypeNames: fixedType ? [fixedType] : appliedFilters.types,
        recipeCategoryNames: appliedFilters.categories,
        isFavorite: appliedFilters.isFavorite,
        hasMadeIt
    };
}

export type RecipesFilterFormValues = {
    recipeTypeNames: string[];
    recipeCategoryNames: string[];
    isFavorite: boolean;
    hasMadeIt: string[];
};

interface RecipesFilterContainerProps {
    fixedType?: RecipeType;
}

export const RecipesFilterContainer = ({ fixedType }: RecipesFilterContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const appliedFilters = useGetAppliedFiltersFromLocation();
    const { data: recipeTypes = [], isPending: isPendingRecipeTypes } = useGetRecipeTypes();
    const { data: recipeCategories = [], isPending: isPendingRecipeCategories } = useGetRecipeCategories();
    const isPending = isPendingRecipeTypes || isPendingRecipeCategories;
    const navigate = useNavigate();
    const location = useLocation();
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [pendingQuery, setPendingQuery] = useState<string>(appliedFilters.query ?? "");
    // Reset pendingQuery whenever the URL query changes (e.g. browser back/forward).
    // Render-phase reset pattern: https://react.dev/reference/react/useState#storing-information-from-previous-renders
    const [lastSyncedQuery, setLastSyncedQuery] = useState<string>(appliedFilters.query ?? "");
    if ((appliedFilters.query ?? "") !== lastSyncedQuery) {
        setLastSyncedQuery(appliedFilters.query ?? "");
        setPendingQuery(appliedFilters.query ?? "");
    }

    useScrollToResults(location.search, containerRef);

    const form = useForm<RecipesFilterFormValues>({
        defaultValues: { ...defaultEmptyFilters, ...parseDefaultRecipeFilterValues(appliedFilters, fixedType) }
    });

    useEffect(() => {
        form.setValue("recipeTypeNames", fixedType ? [fixedType] : []);
    }, [fixedType, form]);

    // Sync form values to URL when the URL changes externally (e.g. nav-menu links
    // to filtered routes). useForm's defaultValues only fire on mount, so without
    // this the form keeps stale state after route-internal navigations.
    useEffect(() => {
        form.reset({ ...defaultEmptyFilters, ...parseDefaultRecipeFilterValues(appliedFilters, fixedType) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, fixedType]);

    const applyFilters = (searchQuery?: string) => {
        const query = searchQuery ?? pendingQuery ?? "";
        const filterValues = form.getValues();
        const params = new URLSearchParams();
        if (query) params.append("query", query);
        if (!fixedType) {
            filterValues.recipeTypeNames.forEach((type) => params.append("type", type));
        }
        filterValues.recipeCategoryNames.forEach((category) => params.append("category", category));
        if (filterValues.isFavorite) params.append("isFavorite", "true");
        if (filterValues.hasMadeIt[0] !== "any") {
            params.append("hasMadeIt", filterValues.hasMadeIt[0]);
        }
        const queryString = params.toString();
        const newPath = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(newPath, { preventScrollReset: true });
    };

    const handleSearchSubmit = (query: string) => {
        applyFilters(query);
    };

    const handleSearchClear = () => {
        setPendingQuery("");
        applyFilters("");
    };

    const clearFilters = () => {
        form.reset(defaultEmptyFilters);
        setPendingQuery("");
        // Use empty values directly since form.reset is async wrt re-render.
        const params = new URLSearchParams();
        navigate(location.pathname + (params.toString() ? `?${params.toString()}` : ""), {
            preventScrollReset: true
        });
    };

    const calculatedFiltersCount = useMemo(() => {
        let count = 0;
        const values = appliedFilters;
        if (values.types.length > 0) count += values.types.length;
        if (values.categories.length > 0) count += values.categories.length;
        if (values.isFavorite) count += 1;
        if (values.hasMadeIt !== null && values.hasMadeIt !== "any") count += 1;
        return count;
    }, [appliedFilters]);
    const FilterIcon = calculatedFiltersCount > 0 ? FunnelIconSolid : FunnelIcon;

    return (
        <div className="recipe-filter-strip w-full bg-[var(--bg-deep)] border-y border-[var(--hairline)]">
            <div
                ref={containerRef}
                className="w-full max-w-4xl mx-auto px-6 sm:px-8 py-5 relative"
            >
                <div className="w-full max-w-2xl mx-auto flex items-center gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                        <RecipeSearchBar
                            value={pendingQuery}
                            onChange={setPendingQuery}
                            onSubmit={handleSearchSubmit}
                            onClear={handleSearchClear}
                        />
                    </div>
                    <Button
                        variant={calculatedFiltersCount > 0 ? "primary" : "tertiary"}
                        onPress={() => setFiltersOpen((v) => !v)}
                        aria-expanded={filtersOpen}
                        aria-controls="recipe-filters"
                        className={
                            calculatedFiltersCount > 0
                                ? "shrink-0"
                                : "shrink-0 [--button-bg:var(--bg)] [--button-bg-hover:var(--bg-muted)] [--button-bg-pressed:var(--bg-muted)] [--button-fg:var(--fg-2)] border border-[var(--hairline)]"
                        }
                    >
                        <FilterIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">Filters</span>
                        {calculatedFiltersCount > 0 ? ` · ${calculatedFiltersCount}` : ""}
                        <ChevronDownIcon
                            className={`w-4 h-4 transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
                        />
                    </Button>
                </div>

                {filtersOpen && (
                    <>
                        <FilterOrnament className="my-5 sm:my-7" />
                        <div id="recipe-filters" className="w-full flex flex-col items-center">
                            {isPending ? null : (
                                <Filters
                                    form={form}
                                    recipeTypes={recipeTypes}
                                    recipeCategories={recipeCategories}
                                    fixedType={fixedType}
                                    onApply={() => applyFilters()}
                                />
                            )}
                        </div>
                    </>
                )}

                <ActiveFilterChips
                    form={form}
                    hasFixedType={!!fixedType}
                    onChange={() => applyFilters()}
                    onClearAll={clearFilters}
                />
            </div>
        </div>
    );
};

const FilterOrnament = ({ className = "" }: { className?: string }) => (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
        <span className="h-px flex-1 max-w-[120px] bg-[var(--fg-2)] opacity-25" />
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-[var(--color-accent)]">
            <path d="M12 3 L13.5 10.5 L21 12 L13.5 13.5 L12 21 L10.5 13.5 L3 12 L10.5 10.5 Z" fill="currentColor" />
        </svg>
        <span className="h-px flex-1 max-w-[120px] bg-[var(--fg-2)] opacity-25" />
    </div>
);
